import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { ModalProps } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import FilePreviewModal from 'components/FilePreviewModal';
import { MessageFileHandlers } from '../MessageFile/MessageFile.component';

import './MessageFilePreviewModal.styles.scss';

type MessageFilePreviewModalProps = {
  fileId: string;
} & Pick<MessageFileHandlers, 'onFilePreview'> &
  ModalProps;

const MessageFilePreviewModal: ForwardRefRenderFunction<
  OverlayRef,
  MessageFilePreviewModalProps
> = (props, ref) => {
  const { className, fileId, onFilePreview, onClose, ...rest } = props;

  const classes = classNames('message-file-preview-modal', className);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const [url, setUrl] = useState('');
  const [type, setType] = useState('');
  const [isUrlFetching, setIsUrlFetching] = useState(true);

  useImperativeHandle(ref, () => modal, [modal]);

  const handleOnClose = useCallback(() => {
    setUrl('');
    setType('');
    onClose?.();
  }, [onClose]);

  const fetchUrl = useCallback(async () => {
    setIsUrlFetching(true);

    try {
      const {
        data: { url, type },
      } = await onFilePreview(fileId);

      if (url) {
        setUrl(url);
        setType(type);
      } else {
        modal?.close();
      }
    } catch {
      modal?.close();
    } finally {
      setIsUrlFetching(false);
    }
  }, [fileId, modal, onFilePreview]);

  useEffect(() => {
    if (modal?.openState && !url && fileId) fetchUrl();
  }, [modal?.openState, url, fileId, fetchUrl]);

  return (
    <FilePreviewModal
      ref={modalRef}
      className={classes}
      type={type}
      url={url}
      isUrlFetching={isUrlFetching}
      onClose={handleOnClose}
      {...rest}
    />
  );
};

export default forwardRef(MessageFilePreviewModal);
